import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token + selected clinic id
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const clinicId = localStorage.getItem('currentClinicId');
    if (clinicId) {
      config.headers['X-Clinic-Id'] = clinicId;
    }
    // Don't set Content-Type for FormData - let axios handle it automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect if we're already on login page or if it's a login request
    const isLoginPage = window.location.pathname === '/login';
    const isPublicPage = window.location.pathname.startsWith('/public/');
    const reqUrl = String(error.config?.url || '');
    const isLoginRequest =
      reqUrl.includes('/auth/login') || reqUrl.includes('/auth/forgot-password');
    
    if (error.response?.status === 401) {
      // Only redirect if not already on login page and not a login request
      if (!isLoginPage && !isPublicPage && !isLoginRequest) {
        localStorage.removeItem('token');
        // Use a small delay to avoid redirect loops
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }, 100);
        toast.error('Session expired. Please login again.');
      }
    } else if (error.response?.status === 403) {
      // GET 403 is often expected (role cannot read a module). Avoid spamming toasts;
      // pages that load optional data gate calls with permissions. Mutations still notify.
      const method = String(error.config?.method || 'get').toLowerCase();
      if (method !== 'get') {
        toast.error(error.response?.data?.message || 'Access denied');
      }
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    }
    
    return Promise.reject(error);
  }
);

export default api;

// API service functions
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  forgotPasswordRequest: (data) => api.post('/auth/forgot-password/request', data),
  forgotPasswordVerifyOtp: (data) => api.post('/auth/forgot-password/verify-otp', data),
  forgotPasswordReset: (data) => api.post('/auth/forgot-password/reset', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/update-profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  uploadProfileImage: (formData) => api.post('/auth/profile-image', formData),
  getMyAttendance: (params) => api.get('/auth/my-attendance', { params }),
  getMyLeaveBalance: (params) => api.get('/auth/my-leave-balance', { params }),
  getMyLeaveRequests: (params) => api.get('/auth/my-leave-requests', { params }),
  createLeaveRequest: (data) => api.post('/auth/leave-requests', data),
  updateMyLeaveRequest: (id, data) => api.patch(`/auth/leave-requests/${id}`, data),
};

export const settingsApi = {
  getEmail: () => api.get('/settings/email'),
  updateEmail: (data) => api.put('/settings/email', data),
  sendTestEmail: (data) => api.post('/settings/email/test', data || {}),
  getTherapistPatientAttendanceAbsent: () => api.get('/settings/therapist-patient-attendance-absent'),
  getTherapistPatientAttendanceAbsentLogs: (params) =>
    api.get('/settings/therapist-patient-attendance-absent/logs', { params }),
  updateTherapistPatientAttendanceAbsent: (data) =>
    api.put('/settings/therapist-patient-attendance-absent', data),
  runTherapistPatientAttendanceAbsentNow: () =>
    api.post('/settings/therapist-patient-attendance-absent/run-now'),
  rebuildStaffLeaveBalances: () => api.post('/settings/staff-leave-balances/rebuild'),
  getStaffLeaveOpening: (params) => api.get('/settings/staff-leave-opening', { params }),
  bulkUpdateStaffLeaveOpening: (data) => api.post('/settings/staff-leave-opening/bulk', data),
  getWhatsAppStatus: () => api.get('/settings/whatsapp/status'),
  getWhatsAppQr: () => api.get('/settings/whatsapp/qr'),
  connectWhatsApp: (data) => api.post('/settings/whatsapp/connect', data || {}),
  disconnectWhatsApp: () => api.post('/settings/whatsapp/disconnect'),
  sendWhatsAppTest: (data) => api.post('/settings/whatsapp/test', data),
  getWhatsAppMessages: () => api.get('/settings/whatsapp/messages'),
  updateWhatsAppMessages: (data) => api.put('/settings/whatsapp/messages', data),
  getWhatsAppBirthdayAutomations: () => api.get('/settings/whatsapp/automations/birthdays'),
  updateWhatsAppBirthdayAutomations: (data) => api.put('/settings/whatsapp/automations/birthdays', data),
  runWhatsAppBirthdayAutomationsNow: (data) =>
    api.post('/settings/whatsapp/automations/birthdays/run-now', data || {}),
  getWhatsAppAutomationLogs: (params) => api.get('/settings/whatsapp/automations/logs', { params }),
};

export const leadsApi = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  updateStatus: (id, data) => api.patch(`/leads/${id}/status`, data),
  convert: (id) => api.post(`/leads/${id}/convert`),
  delete: (id) => api.delete(`/leads/${id}`),
  getSources: () => api.get('/leads/sources'),
  getStatuses: () => api.get('/leads/statuses'),
  getStats: (params) => api.get('/leads/stats', { params }),
  getCommunications: (id) => api.get(`/leads/${id}/communications`),
  addCommunication: (id, data) => api.post(`/leads/${id}/communications`, data),
  checkDuplicateContact: (contact_primary, lead_id) => api.get('/leads/check-duplicate', { 
    params: { contact_primary, lead_id: lead_id || undefined } 
  }),
  getAppointments: (params) => api.get('/leads/appointments', { params }),
  getForDropdown: (params) => api.get('/leads/dropdown', { params }),
};

export const patientsApi = {
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  getHistory: (id) => api.get(`/patients/${id}/history`),
  getTherapyPlans: (id) => api.get(`/patients/${id}/therapy-plans`),
  createTherapyPlan: (id, data) => api.post(`/patients/${id}/therapy-plans`, data),
  getStats: (params) => api.get('/patients/stats', { params }),
  getTodaysBirthdays: (params) => api.get('/patients/birthdays/today', { params }),
  getDocxPrefill: (id, params) => api.get(`/patients/${id}/docx-export/prefill`, { params }),
  exportDocx: (id, data) => api.post(`/patients/${id}/docx-export`, data, { responseType: 'blob' }),
  downloadDocxExport: (patientId, exportId, format = 'pdf', render_style) =>
    api.get(`/patients/${patientId}/docx-exports/${exportId}`, {
      params: { format, ...(render_style ? { render_style } : {}) },
      responseType: 'blob'
    }),
  deleteDocxExport: (patientId, exportId) =>
    api.delete(`/patients/${patientId}/docx-exports/${exportId}`),
};

export const appointmentsApi = {
  getAll: (params) => api.get('/appointments', { params }),
  getById: (id) => api.get(`/appointments/${id}`),
  getCalendar: (params) => api.get('/appointments/calendar', { params }),
  getSlots: (doctorId, date) => api.get(`/appointments/doctor/${doctorId}/slots`, { params: { date } }),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  updateStatus: (id, data) => api.patch(`/appointments/${id}/status`, data),
  reschedule: (id, data) => api.post(`/appointments/${id}/reschedule`, data),
  cancel: (id, data) => api.post(`/appointments/${id}/cancel`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  getStats: (params) => api.get('/appointments/stats', { params }),
};

export const assessmentsApi = {
  getAll: (params) => api.get('/assessments', { params }),
  getById: (id) => api.get(`/assessments/${id}`),
  create: (data) => api.post('/assessments', data),
  update: (id, data) => api.put(`/assessments/${id}`, data),
  delete: (id) => api.delete(`/assessments/${id}`),
  uploadDocument: (id, formData) => api.post(`/assessments/${id}/upload`, formData),
  getDiagnosisCodes: () => api.get('/assessments/diagnosis-codes'),
  getDoctorUsers: () => api.get('/assessments/doctor-users'),
  addDiagnosis: (id, data) => api.post(`/assessments/${id}/diagnoses`, data),
  removeDiagnosis: (id, diagnosisId) => api.delete(`/assessments/${id}/diagnoses/${diagnosisId}`),
  convert: (id, data) => api.post(`/assessments/${id}/convert`, data),
};

export const paymentsApi = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  getPending: (params) => api.get('/payments/pending', { params }),
  record: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id, data) => api.delete(`/payments/${id}`, { data: data || {} }),
  getStats: (params) => api.get('/payments/stats', { params }),
  previewWhatsAppSend: (id) => api.post(`/payments/${id}/whatsapp/preview`),
  sendWhatsApp: (id, data) => api.post(`/payments/${id}/whatsapp/send`, data),
};

export const invoicesApi = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  updateTemplateExport: (id, data) => api.put(`/invoices/${id}/template-export`, data),
  delete: (id, data) => api.delete(`/invoices/${id}`, { data: data || {} }),
  getPdf: (id, templateId, overrides) => {
    if (overrides) {
      return api.post(`/invoices/${id}/pdf`, {
        overrides,
      }, { responseType: 'blob' });
    }
    return api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
  },
  getAttendanceData: (id, params = {}) => api.get(`/invoices/${id}/attendance-data`, { params }),
  send: (id, data) => api.post(`/invoices/${id}/send`, data),
  getDocx: (id, format = 'pdf', render_style, extraParams) =>
    api.get(`/invoices/${id}/docx`, {
      params: { format, ...(render_style ? { render_style } : {}), ...(extraParams || {}) },
      responseType: 'blob'
    }),
  logDownload: (data) => api.post('/invoices/download-log', data),
  getDownloadLogs: (params) => api.get('/invoices/download-logs', { params }),
  previewWhatsAppSend: (id) => api.post(`/invoices/${id}/whatsapp/preview`),
  sendWhatsApp: (id, data) => api.post(`/invoices/${id}/whatsapp/send`, data),
};

export const staffApi = {
  getLeaveRequestsPendingCount: () => api.get('/staff/leave-requests/pending-count'),
  getLeaveRequests: () => api.get('/staff/leave-requests'),
  updateLeaveRequestStatus: (id, data) => api.patch(`/staff/leave-requests/${id}`, data),
  updateLeaveRequest: (id, data) => api.patch(`/staff/leave-requests/${id}/details`, data),
  getAll: () => api.get('/staff'),
  getById: (id) => api.get(`/staff/${id}`),
  getExitLeaveSettlement: (id) => api.get(`/staff/${id}/exit-leave-settlement`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  uploadAgreement: (id, formData) => api.post(`/staff/${id}/agreement`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  resetPassword: (id, data) => api.put(`/staff/${id}/reset-password`, data),
  getAttendance: (params) => api.get('/staff/attendance', { params }),
  getOnLeaveToday: (params) => api.get('/staff/attendance/on-leave-today', { params }),
  markAttendance: (data) => api.post('/staff/attendance', data),
  getAttendanceReport: (params) => api.get('/staff/attendance/report', { params }),
  getDailyRegister: (params) => api.get('/staff/attendance/register', { params }),
  bulkMarkAttendance: (data) => api.post('/staff/attendance/bulk', data),
  bulkMarkAttendanceMonth: (data) => api.post('/staff/attendance/bulk-month', data),
  deleteAttendance: (id) => api.delete(`/staff/attendance/${id}`),
  clearMonthAttendance: (data) => api.post('/staff/attendance/clear-month', data),
  getPayroll: (params) => api.get('/staff/payroll', { params }),
  getPayrollById: (id) => api.get(`/staff/payroll/${id}`),
  previewPayrollLeaveDeduction: (params) => api.get('/staff/payroll/leave-deduction-preview', { params }),
  generatePayroll: (data) => api.post('/staff/payroll/generate', data),
  updatePayroll: (id, data) => api.put(`/staff/payroll/${id}`, data),
  deletePayroll: (id) => api.delete(`/staff/payroll/${id}`),
  /** @param {'pdf'|'docx'} [format] */
  getPaySlip: (id, format = 'pdf') =>
    api.get(`/staff/payroll/${id}/slip`, {
      params: { format },
      responseType: 'blob',
    }),
  getSalaryIncrements: (userId) => api.get(`/staff/${userId}/salary-increments`),
  createSalaryIncrement: (userId, data) => api.post(`/staff/${userId}/salary-increments`, data),
  updateSalaryIncrement: (id, data) => api.put(`/staff/salary-increments/${id}`, data),
  deleteSalaryIncrement: (id) => api.delete(`/staff/salary-increments/${id}`),
};

export const progressApi = {
  getAll: (params) => api.get('/progress-reports', { params }),
  getById: (id) => api.get(`/progress-reports/${id}`),
  getPending: () => api.get('/progress-reports/pending'),
  create: (data) => api.post('/progress-reports', data),
  update: (id, data) => api.put(`/progress-reports/${id}`, data),
  delete: (id) => api.delete(`/progress-reports/${id}`),
  getPdf: (id) => api.get(`/progress-reports/${id}/pdf`, { responseType: 'blob' }),
  send: (id, data) => api.post(`/progress-reports/${id}/send`, data),
  previewWhatsAppSend: (id) => api.post(`/progress-reports/${id}/whatsapp/preview`),
  sendWhatsApp: (id, data) => api.post(`/progress-reports/${id}/whatsapp/send`, data),
};

export const therapyApi = {
  // Therapy Types
  getTypes: (params) => api.get('/therapy/types', { params }),
  getTypeById: (id) => api.get(`/therapy/types/${id}`),
  createType: (data) => api.post('/therapy/types', data),
  updateType: (id, data) => api.put(`/therapy/types/${id}`, data),
  deleteType: (id) => api.delete(`/therapy/types/${id}`),
  // Therapy Plans
  getPlans: (params) => api.get('/therapy/plans', { params }),
  getEndingAlerts: () => api.get('/therapy/plans/alerts'),
  getPlanById: (id) => api.get(`/therapy/plans/${id}`),
  createPlan: (data) => api.post('/therapy/plans', data),
  updatePlan: (id, data) => api.put(`/therapy/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/therapy/plans/${id}`),
  // Doctor-related endpoints
  getDoctorUsers: () => api.get('/therapy/doctors'),
  checkDoctorAvailability: (doctorId, params) => api.get(`/therapy/doctors/${doctorId}/availability`, { params }),
};

export const planTypesApi = {
  getAll: (params) => api.get('/plan-types', { params }),
  getById: (id) => api.get(`/plan-types/${id}`),
  create: (data) => api.post('/plan-types', data),
  update: (id, data) => api.put(`/plan-types/${id}`, data),
  delete: (id) => api.delete(`/plan-types/${id}`),
};

export const doctorsApi = {
  getAll: (params) => api.get('/doctors', { params }),
  getById: (id) => api.get(`/doctors/${id}`),
  checkAvailability: (doctorId, params) => api.get(`/doctors/${doctorId}/availability`, { params }),
};

export const eventsApi = {
  getAll: (params) => api.get('/events', { params }),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  getRegistrations: (id) => api.get(`/events/${id}/registrations`),
  register: (id, data) => api.post(`/events/${id}/register`, data),
  updateRegistration: (id, regId, data) => api.put(`/events/${id}/registrations/${regId}`, data),
  getTrainingPrograms: (params) => api.get('/events/training/programs', { params }),
  getTrainingProgramById: (id) => api.get(`/events/training/programs/${id}`),
  createTrainingProgram: (data) => api.post('/events/training/programs', data),
  updateTrainingProgram: (id, data) => api.put(`/events/training/programs/${id}`, data),
  deleteTrainingProgram: (id) => api.delete(`/events/training/programs/${id}`),
};

export const financeApi = {
  getProfitLoss: (params) => api.get('/finance/profit-loss', { params }),
  getRevenue: (params) => api.get('/finance/revenue', { params }),
  getDashboard: (params) => api.get('/finance/dashboard', { params: params || {} }),
  getExpenses: (params) => api.get('/finance/expenses', { params }),
  getExpenseCategories: () => api.get('/finance/expenses/categories'),
  getExpenseCategoryById: (id) => api.get(`/finance/expenses/categories/${id}`),
  createExpenseCategory: (data) => api.post('/finance/expenses/categories', data),
  updateExpenseCategory: (id, data) => api.put(`/finance/expenses/categories/${id}`, data),
  deleteExpenseCategory: (id) => api.delete(`/finance/expenses/categories/${id}`),
  createExpense: (data) => api.post('/finance/expenses', data),
  updateExpense: (id, data) => api.put(`/finance/expenses/${id}`, data),
};

export const filesApi = {
  upload: (formData, entityType, entityId) => {
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getEntityFiles: (type, id) => api.get(`/files/entity/${type}/${id}`),
  download: (id) => api.get(`/files/download/${id}`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/files/${id}`),
};

export const patientAttendanceApi = {
  getActivePatients: () => api.get('/patient-attendance/patients'),
  getAttendance: (params) => api.get('/patient-attendance', { params }),
  markAttendance: (data) => api.post('/patient-attendance', data),
  bulkMarkAttendance: (data) => api.post('/patient-attendance/bulk', data),
  bulkMarkAttendanceMonth: (data) => api.post('/patient-attendance/bulk-month', data),
  getDailyRegister: (params) => api.get('/patient-attendance/register', { params }),
  getAttendanceReport: (params) => api.get('/patient-attendance/report', { params }),
  getAttendanceByDate: (date) => api.get(`/patient-attendance/date/${date}`),
  getPatientHistory: (patientId, params) => api.get(`/patient-attendance/patient/${patientId}/history`, { params }),
  getAuditLogs: (params) => api.get('/patient-attendance/audit-logs', { params }),
  deleteAttendance: (id) => api.delete(`/patient-attendance/${id}`),
  clearMonthAttendance: (data) => api.post('/patient-attendance/clear-month', data),
  getTotalAssumptionAmount: (params) => api.get('/patient-attendance/total-assumption', { params }),
  getTodaysAssumptionAmount: (params) => api.get('/patient-attendance/today-assumption', { params }),
};

export const protocolsApi = {
  getAll: (params) => api.get('/protocols', { params }),
  create: (data) => api.post('/protocols', data),
  update: (id, data) => api.put(`/protocols/${id}`, data),
  delete: (id) => api.delete(`/protocols/${id}`),
};

export const leaveTypesApi = {
  getAll: () => api.get('/leave-types'),
  getActive: () => api.get('/leave-types/active'),
  getById: (id) => api.get(`/leave-types/${id}`),
  create: (data) => api.post('/leave-types', data),
  setupMonthlyPair: (data) => api.post('/leave-types/setup-monthly-pair', data),
  createHalfDayCompanion: () => api.post('/leave-types/half-day-companion'),
  update: (id, data) => api.put(`/leave-types/${id}`, data),
  delete: (id) => api.delete(`/leave-types/${id}`),
};

export const diagnosisApi = {
  getAll: () => api.get('/diagnosis/codes'),
  getActive: () => api.get('/diagnosis/codes/active'),
  getById: (id) => api.get(`/diagnosis/codes/${id}`),
  create: (data) => api.post('/diagnosis/codes', data),
  update: (id, data) => api.put(`/diagnosis/codes/${id}`, data),
  delete: (id) => api.delete(`/diagnosis/codes/${id}`),
};

export const leadConfigApi = {
  // Lead Sources
  getSources: () => api.get('/lead-config/sources'),
  getActiveSources: () => api.get('/lead-config/sources/active'),
  getSourceById: (id) => api.get(`/lead-config/sources/${id}`),
  createSource: (data) => api.post('/lead-config/sources', data),
  updateSource: (id, data) => api.put(`/lead-config/sources/${id}`, data),
  deleteSource: (id) => api.delete(`/lead-config/sources/${id}`),
  getPublicFormLink: () => api.get('/lead-config/public-form'),
  regeneratePublicFormLink: () => api.post('/lead-config/public-form/regenerate'),
  // Lead Statuses
  getStatuses: () => api.get('/lead-config/statuses'),
  getActiveStatuses: () => api.get('/lead-config/statuses/active'),
  getStatusById: (id) => api.get(`/lead-config/statuses/${id}`),
  createStatus: (data) => api.post('/lead-config/statuses', data),
  updateStatus: (id, data) => api.put(`/lead-config/statuses/${id}`, data),
  deleteStatus: (id) => api.delete(`/lead-config/statuses/${id}`),
};

export const invoiceItemsApi = {
  getAll: (params) => api.get('/invoice-items', { params }),
  getById: (id) => api.get(`/invoice-items/${id}`),
  create: (data) => api.post('/invoice-items', data),
  update: (id, data) => api.put(`/invoice-items/${id}`, data),
  delete: (id) => api.delete(`/invoice-items/${id}`),
};

export const rolesApi = {
  getAll: () => api.get('/roles'),
  getList: () => api.get('/roles/list'),
  getModules: () => api.get('/roles/modules'),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

export const jobInquiriesApi = {
  getAll: (params) => api.get('/job-inquiries', { params }),
  getById: (id) => api.get(`/job-inquiries/${id}`),
  create: (data, file) => {
    const formData = new FormData();
    
    // Add all data fields
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    
    // Add file if provided
    if (file) {
      formData.append('resume', file);
    }
    
    return api.post('/job-inquiries', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id, data, file) => {
    const formData = new FormData();
    
    // Add all data fields
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    
    // Add file if provided
    if (file) {
      formData.append('resume', file);
    }
    
    return api.put(`/job-inquiries/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateStatus: (id, status) => api.patch(`/job-inquiries/${id}/status`, { status }),
  delete: (id) => api.delete(`/job-inquiries/${id}`),
  downloadResume: (id) => api.get(`/job-inquiries/${id}/resume`),
};

export const jobPositionTypesApi = {
  getAll: () => api.get('/job-position-types'),
  getActive: () => api.get('/job-position-types/active'),
  create: (data) => api.post('/job-position-types', data),
  update: (id, data) => api.put(`/job-position-types/${id}`, data),
  delete: (id) => api.delete(`/job-position-types/${id}`),
};

export const clinicsApi = {
  getMine: () => api.get('/clinics/me'),
  getAll: () => api.get('/clinics'),
  getById: (id) => api.get(`/clinics/${id}`),
  create: (data) => api.post('/clinics', data),
  update: (id, data) => api.put(`/clinics/${id}`, data),
  delete: (id) => api.delete(`/clinics/${id}`),
  getMembers: (id) => api.get(`/clinics/${id}/members`),
  addMember: (id, data) => api.post(`/clinics/${id}/members`, data),
  updateMember: (id, userId, data) => api.put(`/clinics/${id}/members/${userId}`, data),
  removeMember: (id, userId) => api.delete(`/clinics/${id}/members/${userId}`),
};

export const docxTemplatesApi = {
  getAll: () => api.get('/docx-templates'),
  getActive: () => api.get('/docx-templates/active'),
  getPlaceholders: (shape) => api.get(`/docx-templates/placeholders/${shape}`),
  create: (formData) => api.post('/docx-templates', formData),
  update: (id, data) => api.patch(`/docx-templates/${id}`, data),
  activate: (id) => api.patch(`/docx-templates/${id}/activate`),
  remove: (id) => api.delete(`/docx-templates/${id}`),
  download: (id) => api.get(`/docx-templates/${id}/download`, { responseType: 'blob' }),
};

export const discussionsApi = {
  getUsers: () => api.get('/discussions/users'),
  getChannels: () => api.get('/discussions/channels'),
  createChannel: (data) => api.post('/discussions/channels', data),
  updateChannel: (channelId, data) => api.patch(`/discussions/channels/${channelId}`, data),
  deleteChannel: (channelId) => api.delete(`/discussions/channels/${channelId}`),
  getMessages: (channelId) => api.get(`/discussions/channels/${channelId}/messages`),
  getChannelMembers: (channelId) => api.get(`/discussions/channels/${channelId}/members`),
  sendMessage: (channelId, data) => api.post(`/discussions/channels/${channelId}/messages`, data),
  uploadAttachment: (channelId, file) => {
    const formData = new FormData();
    formData.append('channel_id', String(channelId));
    formData.append('file', file);
    return api.post('/discussions/attachments/upload', formData);
  },
  presignAttachment: (data) => api.post('/discussions/attachments/presign', data),
  getNotifications: () => api.get('/discussions/notifications'),
  readNotification: (id) => api.patch(`/discussions/notifications/${id}/read`),
  readAllNotifications: () => api.patch('/discussions/notifications/read-all'),
};

export const monthlyReviewsApi = {
  getAll: (params) => api.get('/monthly-reviews', { params }),
  getById: (id) => api.get(`/monthly-reviews/${id}`),
  create: (data) => api.post('/monthly-reviews', data),
  update: (id, data) => api.put(`/monthly-reviews/${id}`, data),
  delete: (id) => api.delete(`/monthly-reviews/${id}`),
  getVisibility: () => api.get('/monthly-reviews/visibility'),
  setVisibility: (data) => api.put('/monthly-reviews/visibility', data),
};

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const publicLeadsApi = {
  getFormMeta: (token) => publicApi.get(`/public/leads/form/${token}`),
  submit: (token, data) => publicApi.post(`/public/leads/form/${token}`, data),
};

